/**
 * Lead Finder → "Add to list": the chosen results into a data list — an existing static list
 * (each result a row; its columns matched to the result's fields, adjustable) or contacts list
 * (each result becomes a contact and joins it), or a new list named here (static with Lead
 * Finder's columns, or a contacts list).
 */
import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, Modal, ScrollView, TextInput, ActivityIndicator, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Button } from '../design-system';
import { Chip } from './leadFinderUi';
import { apiService, DataList } from '../../services/api';
import { useLocale } from '../../contexts/LocaleContext';
import { useAppContext } from '../../contexts/AppContext';
import { useRTLStyles } from '../../hooks/useRTLStyles';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { colors, spacing, typography } from '../../theme';
import { designTokens } from '../../theme/design-system';
import type { TranslationKey } from '../../i18n/en';

interface Props {
  visible: boolean;
  runId: string;
  ids: string[];
  onClose: () => void;
  onDone: (text: string, failed: number) => void;
}

type Kind = 'static' | 'contacts';

export const AddToListModal: React.FC<Props> = ({ visible, runId, ids, onClose, onDone }) => {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const { navigateTo } = useAppContext();

  const [lists, setLists] = useState<DataList[] | null>(null);
  const [locked, setLocked] = useState(false);
  const [mode, setMode] = useState<'existing' | 'new'>('existing');
  const [listId, setListId] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [newKind, setNewKind] = useState<Kind>('static');
  const [columns, setColumns] = useState<Array<{ id: string; name: string; type: string }>>([]);
  const [fields, setFields] = useState<string[]>([]);
  const [fieldLabels, setFieldLabels] = useState<Record<string, string>>({});
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [loadingMap, setLoadingMap] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) return;
    setError(null);
    setListId(null);
    setNewName('');
    setColumns([]);
    apiService.getDataLists({ limit: 100 }).then((res) => {
      if (res.statusCode === 1000 && Array.isArray(res.data)) {
        const usable = res.data.filter((l) => l.dataSource !== 'webhook' && (l.columnsSource !== 'system' || l.systemModule === 'contacts'));
        setLists(usable);
        setMode(usable.length ? 'existing' : 'new');
      } else {
        setLists([]);
        setMode('new');
        if ((res.errors?.[0] as any)?.code === 'FEATURE_NOT_IN_PLAN') setLocked(true);
      }
    }).catch(() => { setLists([]); setMode('new'); });
  }, [visible]);

  const chosen = lists?.find((l) => l._id === listId);
  const chosenKind: Kind | undefined = chosen ? (chosen.columnsSource === 'system' ? 'contacts' : 'static') : undefined;

  const pick = async (id: string) => {
    setListId(id);
    setColumns([]);
    const list = lists?.find((l) => l._id === id);
    if (!list || list.columnsSource === 'system') return;
    setLoadingMap(true);
    try {
      const res = await apiService.getLeadListMapping(id, runId);
      if (res.statusCode === 1000 && res.data) {
        setColumns(res.data.columns);
        setFields(res.data.fields);
        setFieldLabels(res.data.fieldLabels || {});
        setMapping(res.data.mapping);
      }
    } finally {
      setLoadingMap(false);
    }
  };

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const body = mode === 'new'
        ? { action: 'data_list' as const, ids, newList: { name: newName.trim(), kind: newKind } }
        : { action: 'data_list' as const, ids, listId: listId!, ...(chosenKind === 'static' ? { mapping } : {}) };
      const res = await apiService.actOnLeadResults(runId, body);
      if (res.statusCode === 1000 && res.data) {
        onDone(t('leadFinder.addList.done', { count: res.data.done, name: res.data.list?.name || '' }), res.data.failed?.length || 0);
      } else {
        setError(res.errors?.[0]?.message || t('leadFinder.error.act'));
      }
    } catch (err: any) {
      setError(err?.message || t('leadFinder.error.act'));
    } finally {
      setSaving(false);
    }
  };

  const canSubmit = !saving && (mode === 'new' ? newName.trim().length > 0 : !!listId && (chosenKind !== 'static' || Object.values(mapping).some(Boolean)));

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <View style={styles.modal}>
          <Text style={[styles.title, { textAlign: rtl.textStart }]}>{t('leadFinder.addList.title', { count: ids.length })}</Text>
          {locked ? (
            <View style={styles.gap}>
              <Text style={styles.meta}>{t('leadFinder.addList.locked')}</Text>
              <Button title={t('leadFinder.locked.upgrade')} onPress={() => { onClose(); navigateTo('subscription'); }} variant="primary" />
            </View>
          ) : lists === null ? <ActivityIndicator color={colors.primary} /> : (
            <ScrollView style={styles.body} contentContainerStyle={styles.gap}>
              <View style={[styles.wrap, { flexDirection: rtl.rowDirection }]}>
                {lists.length ? <Chip label={t('leadFinder.addList.existing')} selected={mode === 'existing'} onPress={() => setMode('existing')} /> : null}
                <Chip label={t('leadFinder.addList.new')} icon="add" selected={mode === 'new'} onPress={() => setMode('new')} />
              </View>

              {mode === 'new' ? (
                <>
                  <TextInput value={newName} onChangeText={setNewName} placeholder={t('leadFinder.addList.namePlaceholder')}
                    placeholderTextColor={colors.text.secondary as string} style={[styles.input, { textAlign: rtl.textStart }]} maxLength={120} />
                  <View style={[styles.wrap, { flexDirection: rtl.rowDirection }]}>
                    {(['static', 'contacts'] as const).map((k) => (
                      <Chip key={k} icon={k === 'static' ? 'grid-outline' : 'people-outline'} label={t(`leadFinder.addList.kind.${k}` as TranslationKey)} selected={newKind === k} onPress={() => setNewKind(k)} />
                    ))}
                  </View>
                  <Text style={styles.meta}>{t(newKind === 'static' ? 'leadFinder.addList.staticHint' : 'leadFinder.addList.contactsHint')}</Text>
                </>
              ) : (
                <>
                  {lists.map((l) => (
                    <TouchableOpacity key={l._id} style={[styles.listRow, listId === l._id && styles.listRowOn, { flexDirection: rtl.rowDirection }]} onPress={() => pick(l._id)}>
                      <Ionicons name={l.columnsSource === 'system' ? 'people-outline' : 'grid-outline'} size={18} color={colors.primary as string} />
                      <Text style={[styles.listName, { textAlign: rtl.textStart }]} numberOfLines={1}>{l.name}</Text>
                      <Text style={styles.meta}>{t(l.columnsSource === 'system' ? 'leadFinder.addList.kind.contacts' : 'leadFinder.addList.kind.static')}</Text>
                    </TouchableOpacity>
                  ))}
                  {chosenKind === 'contacts' ? <Text style={styles.meta}>{t('leadFinder.addList.contactsHint')}</Text> : null}
                  {chosenKind === 'static' ? (
                    loadingMap ? <ActivityIndicator color={colors.primary} /> : (
                      <View style={styles.gap}>
                        <Text style={[styles.label, { textAlign: rtl.textStart }]}>{t('leadFinder.addList.mapping')}</Text>
                        {columns.map((c) => (
                          <View key={c.id} style={styles.mapRow}>
                            <Text style={[styles.colName, { textAlign: rtl.textStart }]}>{c.name}</Text>
                            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={[styles.wrap, { flexDirection: rtl.rowDirection }]}>
                              <Chip label={t('leadFinder.addList.skip')} selected={!mapping[c.id]} onPress={() => setMapping((m) => ({ ...m, [c.id]: '' }))} />
                              {fields.map((f) => (
                                <Chip key={f} label={fieldLabels[f] || t(`leadFinder.listField.${f}` as TranslationKey)} selected={mapping[c.id] === f} onPress={() => setMapping((m) => ({ ...m, [c.id]: f }))} />
                              ))}
                            </ScrollView>
                          </View>
                        ))}
                      </View>
                    )
                  ) : null}
                </>
              )}
              {error ? <Text style={styles.error}>{error}</Text> : null}
            </ScrollView>
          )}
          {!locked ? <Button title={t('leadFinder.addList.add')} onPress={submit} variant="primary" loading={saving} disabled={!canSubmit} /> : null}
          <Button title={t('common.cancel')} onPress={onClose} variant="ghost" />
        </View>
      </View>
    </Modal>
  );
};

const getStyles = () => StyleSheet.create({
  backdrop: { flex: 1, backgroundColor: colors.background.overlay, justifyContent: 'center', alignItems: 'center', padding: spacing.lg },
  modal: { width: '100%', maxWidth: 560, maxHeight: '88%', backgroundColor: colors.background.surface, borderRadius: designTokens.borderRadius.lg, padding: spacing.lg, gap: spacing.sm },
  body: { flexGrow: 0 },
  gap: { gap: spacing.sm },
  title: { fontSize: typography.fontSize.lg, color: colors.text.primary, fontFamily: typography.fontFamily.semibold },
  label: { fontSize: typography.fontSize.sm, color: colors.text.primary, fontFamily: typography.fontFamily.semibold },
  meta: { fontSize: typography.fontSize.xs, color: colors.text.secondary, fontFamily: typography.fontFamily.regular },
  error: { fontSize: typography.fontSize.sm, color: colors.error, fontFamily: typography.fontFamily.regular },
  wrap: { flexWrap: 'wrap', gap: spacing.xs },
  input: {
    borderWidth: 1, borderColor: colors.border, borderRadius: designTokens.borderRadius.md, paddingHorizontal: spacing.sm, paddingVertical: spacing.sm,
    fontSize: typography.fontSize.base, color: colors.text.primary, fontFamily: typography.fontFamily.regular, backgroundColor: colors.background.primary,
  },
  listRow: { alignItems: 'center', gap: spacing.sm, padding: spacing.sm, borderRadius: designTokens.borderRadius.md, borderWidth: 1, borderColor: colors.border },
  listRowOn: { borderColor: colors.primary, backgroundColor: String(colors.primary) + '10' },
  listName: { flex: 1, fontSize: typography.fontSize.base, color: colors.text.primary, fontFamily: typography.fontFamily.regular },
  mapRow: { gap: 4 },
  colName: { fontSize: typography.fontSize.sm, color: colors.text.primary, fontFamily: typography.fontFamily.medium },
});
